import React, { useState, useMemo, useEffect } from "react";
import { Select, DatePicker, notification, Pagination } from "antd";
import { LuFilter, LuX } from "react-icons/lu";
import AssignmentCard from "../../../components/common/AssignmentCard";
import {
  selectCurrentRole,
  selectCurrentUser,
} from "../../../store/auth/authSlice";
import {
  useGetAllAssignmentsQuery,
  useGetAssignmentStatsQuery,
  useGetInterpreterAssignmentsQuery,
  useGetOrganizationsQuery,
  useGetAllInterpretersQuery,
  useGetInterpretersQuery,
} from "../../../store/api/apiSlice";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  PAGINATION_PAGE_SIZE,
  shouldShowPagination,
} from "../../../utils/pagination";
import { getAssignmentOverviewStats, mergeAssignmentLists } from "./helpers";
import {
  assignmentMatchesListFilters,
  getInterpreterRecordId,
  isCompletedAssignmentStatus,
  rememberSubmittedAssignment,
} from "../../../utils/interpreterAssignment";

const getDefaultFilters = (userRole, interpreterId) => ({
  organization: undefined,
  start_date: undefined,
  end_date: undefined,
  sort: "newest",
  interpreter: userRole === "interpreter" ? interpreterId : undefined,
  status: userRole === "interpreter" ? "all" : "not_assigned",
  search: undefined,
});

const buildAssignmentQueryParams = (filters) => {
  const params = {};

  if (filters.organization) params.organization = filters.organization;
  
  if (filters.start_date) params.start_date = filters.start_date;
  
  if (filters.end_date) params.end_date = filters.end_date;
  
  if (filters.sort) params.sort = filters.sort;
  
  if (filters.interpreter) params.interpreter = filters.interpreter;
  
  if (filters.status && filters.status !== "all") {
    params.status = filters.status;
  }
  
  if (filters.search) params.search = filters.search;

  return params;
};

const ManageAssignments = () => {
  const userRole = useSelector(selectCurrentRole);
  const currentUser = useSelector(selectCurrentUser);
  const interpreterId = getInterpreterRecordId(currentUser);
  const [dateRange, setDateRange] = useState(null);

  const [filters, setFilters] = useState(() =>
    getDefaultFilters(userRole, interpreterId),
  );

  useEffect(() => {
    if (userRole !== "interpreter") return;

    setFilters((prev) => {
      const nextInterpreter =
        interpreterId && prev.interpreter !== interpreterId
          ? interpreterId
          : prev.interpreter;
      const nextStatus =
        prev.status === "complete" ? "all" : prev.status;

      if (
        nextInterpreter === prev.interpreter &&
        nextStatus === prev.status
      ) {
        return prev;
      }

      return {
        ...prev,
        interpreter: nextInterpreter,
        status: nextStatus,
      };
    });
  }, [userRole, interpreterId]);

  const [activeFilters, setActiveFilters] = useState(false);
  const [locallyAssignedIds, setLocallyAssignedIds] = useState([]);
  const [assignmentPatches, setAssignmentPatches] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = PAGINATION_PAGE_SIZE;

  const queryParams = useMemo(() => {
    if (userRole === "interpreter") {
      const params = {};
      if (interpreterId) params.interpreter = interpreterId;
      if (filters.organization) params.organization = filters.organization;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      params.status =
        filters.status &&
        filters.status !== "all" &&
        filters.status !== "complete"
          ? filters.status
          : "all";
      return params;
    }

    return buildAssignmentQueryParams(filters);
  }, [userRole, interpreterId, filters]);

  const queryParamsIn = useMemo(() => {
    const params = {
      search: "",
    };
    return params;
  }, []);

  // Fetch organizations for dropdown
  const { data: organizationsData } = useGetOrganizationsQuery({});

  // Fetch interpreters for dropdown
  const { data: interpretersData } = useGetInterpretersQuery(queryParamsIn);

  // Fetch assignments based on role and apply query params
  const {
    data: interpreterAssignments,
    isLoading: isInterpreterLoading,
    refetch,
  } = useGetAllAssignmentsQuery(queryParams, {
    skip: userRole === "interpreter" && !interpreterId,
  });

  const {
    data: ownAssignmentsByInterpreterId,
    isLoading: isOwnAssignmentsLoading,
    refetch: refetchOwnAssignments,
  } = useGetInterpreterAssignmentsQuery(interpreterId, {
    skip: userRole !== "interpreter" || !interpreterId,
  });

  const isAssignmentsLoading =
    isInterpreterLoading || isOwnAssignmentsLoading;

  const { data: statsData, isLoading: isStatsLoading } =
    useGetAssignmentStatsQuery();

  const stats = useMemo(
    () => getAssignmentOverviewStats(statsData),
    [statsData],
  );

  const selectedInterpreter = useMemo(() => {
    if (!filters.interpreter || !interpretersData?.data) return null;

    return (
      interpretersData.data.find(
        (interpreter) => interpreter._id === filters.interpreter,
      ) || null
    );
  }, [filters.interpreter, interpretersData]);

  // Get assignments with safe array checks to prevent undefined errors and ensure proper data structure
  const assignments = useMemo(() => {
    const list =
      userRole === "interpreter"
        ? mergeAssignmentLists(
            interpreterAssignments,
            ownAssignmentsByInterpreterId,
          )
        : mergeAssignmentLists(interpreterAssignments);

    return list.map((item) => {
      const patch = assignmentPatches[item._id];

      if (!patch) return item;

      const existingAssigned = Array.isArray(item.assignment)
        ? item.assignment
        : [];
      const patchedAssigned = Array.isArray(patch.assignment)
        ? patch.assignment
        : [];
      const nextStatus = isCompletedAssignmentStatus(item.status)
        ? item.status
        : isCompletedAssignmentStatus(patch.status)
          ? patch.status
          : patch.status || item.status;

      return {
        ...item,
        status: nextStatus,
        assignment: patchedAssigned.length ? patchedAssigned : existingAssigned,
      };
    });
  }, [
    userRole,
    interpreterAssignments,
    ownAssignmentsByInterpreterId,
    assignmentPatches,
  ]);

  const filteredAssignments = useMemo(() => {
    return assignments.filter((item) => {
      if (userRole === "interpreter") {
        if (isCompletedAssignmentStatus(item?.status)) {
          return false;
        }

        if (filters.organization) {
          const organizationId =
            item?.organization?._id || item?.organization;
          if (String(organizationId) !== String(filters.organization)) {
            return false;
          }
        }

        return assignmentMatchesListFilters(
          item,
          { ...filters, interpreter: undefined },
        );
      }

      if (
        filters.status === "not_assigned" &&
        locallyAssignedIds.includes(item._id)
      ) {
        return false;
      }

      return assignmentMatchesListFilters(item, filters, {
        interpreterUserId: selectedInterpreter?.user?._id,
      });
    });
  }, [assignments, locallyAssignedIds, filters, selectedInterpreter, userRole]);

  // Get paginated assignments
  const paginatedAssignments = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAssignments.slice(startIndex, endIndex);
  }, [filteredAssignments, currentPage, pageSize]);

  // Handle pagination change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of the page when changing pages
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Prepare organization options for select
  const organizationOptions = useMemo(() => {
    if (!organizationsData?.data) return [];
    return organizationsData.data.map((org) => ({
      value: org._id,
      label: org.name,
    }));
  }, [organizationsData]);

  // Prepare interpreter options for select
  const interpreterOptions = useMemo(() => {
    if (!interpretersData?.data) return [];
    return interpretersData.data
      .filter((interpreter) => interpreter.status?.toLowerCase() !== "inactive")
      .map((interpreter) => ({
        value: interpreter._id,
        label: interpreter.user?.full_name || interpreter.user?.full_name,
      }));
  }, [interpretersData]);

  const handleAssignSuccess = (payload) => {
    const assignedId = payload?.assignmentId;
    const interpreterId = payload?.interpreterId;

    if (assignedId && interpreterId) {
      setAssignmentPatches((prev) => ({
        ...prev,
        [assignedId]: {
          status: "assigned",
          assignment: [{ _id: interpreterId }],
        },
      }));
    }

    if (assignedId && filters.status === "not_assigned") {
      setLocallyAssignedIds((prev) =>
        prev.includes(assignedId) ? prev : [...prev, assignedId],
      );
    }

    refetch();
    refetchOwnAssignments?.();
  };

  const handleTranslationSubmitted = (record) => {
    if (!record?._id) return;

    rememberSubmittedAssignment(interpreterId, {
      ...record,
      status: "complete",
    });

    setAssignmentPatches((prev) => ({
      ...prev,
      [record._id]: {
        ...prev[record._id],
        status: "complete",
      },
    }));
    refetch();
    refetchOwnAssignments?.();
  };

  // Status options for filter
  const statusOptions = [
    { value: "all", label: "All " },
    { value: "assigned", label: "Assigned" },

    { value: "complete", label: "Completed" },
    // { value: 'not_active', label: 'Not Active' },
    // { value: 'not_active', label: 'Not Active' },

    // { value: 'over_due', label: 'Overdue' },
    {
      value: "not_assigned",
      label: "Unassigned",
    },
  ];

  // Sort options for filter
  const sortOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
  ];

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value || undefined,
    }));
    setCurrentPage(1);
    setActiveFilters(true);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setCurrentPage(1);

    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        start_date: dates[0].startOf("day").unix(),
        end_date: dates[1].endOf("day").unix(),
      }));
      setActiveFilters(true);
      return;
    }

    setFilters((prev) => ({
      ...prev,
      start_date: undefined,
      end_date: undefined,
    }));
  };

  const clearFilters = () => {
    setFilters(getDefaultFilters(userRole, interpreterId));
    setDateRange(null);
    setCurrentPage(1);
    setActiveFilters(false);
  };

  // Prepare values for DatePicker if start_date and end_date are set
  // const dateRange = useMemo(() => {
  //   if (filters.start_date && filters.end_date) {
  //     return [
  //       moment.unix(filters.start_date),
  //       moment.unix(filters.end_date)
  //     ];
  //   }
  //   return null;
  // }, [filters.start_date, filters.end_date]);

  return (
    <div className="space-y-6">
      {/* Overview Section */}
      <div>
        <div className="flex justify-between items-center mb-2 selectSecondary">
          <h2 className="text-md font-semibold m-0">Overview</h2>
        </div>
        {userRole !== "interpreter" && (
          <div className="grid md:grid-cols-4 grid-cols-2 gap-2">
            {stats?.map((stat, index) => (
              <div key={index} className="bg-[#F9FAFB] rounded-xl p-4">
                <div className="text-2xl font-bold">
                  {isStatsLoading ? "—" : stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5  gap-2 sm:flex sm:items-center ">
        <Select
          placeholder="Organisation"
          className="h-12 md:w-1/4 "
          value={filters.organization || undefined}
          options={organizationOptions}
          allowClear
          onChange={(value) => handleFilterChange("organization", value)}
        />
        {/* <DatePicker.RangePicker
          allowClear
          placeholder={["Select a date range--"]}
          suffixIcon={<LuCalendarDays className="text-gray-400" size={20} />}
          className="h-12 w-full rounded-2xl border border-gray-200 px-4"
          onChange={onDateChange}
          format="YYYY-MM-DD"
          separator="-"
          popupClassName="date-range-popup-mobile"
          disabled={isLoading}
          getPopupContainer={() => document.body} // Changed to use document.body
          dropdownAlign={{
            points: ['tl', 'bl'], // Align the top-left of popup with bottom-left of input
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
          style={{ width: '100%' }}
        /> */}
        <DatePicker.RangePicker
          className="h-12 w-full md:w-1/4 "
          value={dateRange}
          onChange={handleDateChange}
          allowClear
          format="YYYY-MM-DD"
          separator="-"
          placeholder={["Select a date range--"]}
          popupClassName="date-range-popup-mobile"
          getPopupContainer={() => document.body} // Changed to use document.body
          dropdownAlign={{
            points: ["tl", "bl"], // Align the top-left of popup with bottom-left of input
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
        />

        <Select
          placeholder="Sort by"
          className="h-12 w-full md:w-1/4 "
          value={filters.sort || undefined}
          options={sortOptions}
          allowClear
          onChange={(value) => handleFilterChange("sort", value)}
        />
        {userRole !== "interpreter" && (
          <Select
            placeholder="Interpreter"
            className="h-12 w-full md:w-1/4 "
            value={filters.interpreter || undefined}
            options={interpreterOptions}
            allowClear
            onChange={(value) => handleFilterChange("interpreter", value)}
          />
        )}
        {userRole === "interpreter" ? (
          <Select
            placeholder="Status"
            className="h-12 w-full md:w-1/4 "
            value={filters.status || "all"}
            options={[
              { value: "all", label: "All" },
              { value: "assigned", label: "Assigned" },
            ]}
            onChange={(value) => handleFilterChange("status", value)}
          />
        ) : (
          <Select
            placeholder="Status"
            className="h-12 w-full md:w-1/4 "
            value={filters.status || undefined}
            options={statusOptions}
            onChange={(value) => handleFilterChange("status", value)}
          />
        )}
        <button
          className={`h-12 w-16 flex items-center justify-center border rounded-xl ${
            activeFilters ? "bg-primary text-white" : ""
          }`}
          onClick={activeFilters ? clearFilters : null}
        >
          {activeFilters ? (
            <LuX size={20} />
          ) : (
            <LuFilter className="text-red-500" />
          )}
        </button>
      </div>

      {/* Assignments */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {paginatedAssignments.length > 0 ? (
          paginatedAssignments.map((assignment, index) => (
            <AssignmentCard
              key={assignment._id || index}
              assignment={assignment}
              interpreter={selectedInterpreter}
              onAssign={handleAssignSuccess}
              onTranslationSubmitted={handleTranslationSubmitted}
              showFooter={true}
              showAssignContainer={false}
            />
          ))
        ) : isAssignmentsLoading ? (
          <div className="col-span-full text-center py-10">
            Loading assignments...
          </div>
        ) : (
          <div className="col-span-full text-center py-10 text-gray-500">
            {activeFilters
              ? "No assignments match your filter criteria."
              : "No assignments available."}
          </div>
        )}
      </div>

      {/* Pagination */}
      {shouldShowPagination(filteredAssignments) && (
        <div className="flex justify-end mt-8">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredAssignments.length}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default ManageAssignments;
